import React from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { IChangeRecord } from "./ChangeHistoryTimeline.types";
import { ACTION_COLOR_MAP } from "./constants";
import { formatDateTimeWithLocale } from "../../helpers";
import FieldChangeRow from "./FieldChangeRow";

interface TimelineEntryProps {
  record: IChangeRecord;
}

/**
 * TimelineEntry renders a single change record as a Paper card with:
 * - Timestamp, user name, action chip, entry ID, sub_module_name
 * - Filtered field changes (skips entries where old and new are deeply equal)
 */
const TimelineEntry: React.FC<TimelineEntryProps> = ({ record }) => {
  const actionColor = ACTION_COLOR_MAP[record.action] || "#757575";

  /**
   * Filter changes to skip entries where old and new values are deeply equal.
   * Uses JSON.stringify for deep comparison.
   */
  const getFilteredChanges = () => {
    if (!record.changes) return [];

    return Object.entries(record.changes).filter(([, fieldChange]) => {
      return JSON.stringify(fieldChange.old) !== JSON.stringify(fieldChange.new);
    });
  };

  const filteredChanges = getFilteredChanges();

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        borderLeft: `4px solid ${actionColor}`,
        borderRadius: 1,
      }}
    >
      {/* Header: Timestamp + Action Chip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {formatDateTimeWithLocale(record.create_ts)}
        </Typography>
        <Chip
          label={record.action}
          size="small"
          sx={{
            backgroundColor: actionColor,
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.7rem",
          }}
        />
      </Box>

      {/* User name */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {record.user_name}
      </Typography>

      {/* Entry ID and sub_module_name */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Entry ID: {record.entry_id}
        </Typography>
        {record.sub_module_name && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            • {record.sub_module_name}
          </Typography>
        )}
      </Box>

      {/* Field changes */}
      {!record.changes ? (
        <Typography variant="body2" sx={{ color: "text.disabled", fontStyle: "italic" }}>
          No field changes recorded
        </Typography>
      ) : filteredChanges.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.disabled", fontStyle: "italic" }}>
          No field changes recorded
        </Typography>
      ) : (
        <Box sx={{ mt: 1 }}>
          {filteredChanges.map(([fieldName, fieldChange]) => (
            <FieldChangeRow
              key={fieldName}
              fieldName={fieldName}
              oldValue={fieldChange.old}
              newValue={fieldChange.new}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default TimelineEntry;
